import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useRoute, useSearch } from "wouter";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ArticleRenderer } from "@/components/article-renderer";
import { HighlightedSnippet } from "@/components/highlighted-snippet";
import { SuggestionChips } from "@/components/knowledge/suggestion-chip";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  ArrowRight,
  Star,
  ShieldCheck,
  Award,
  Scale,
  BookOpen,
  AlertTriangle,
  Search,
  X,
} from "lucide-react";
import type { KnowledgeArticle, KnowledgeSection, KnowledgeSuggestion, PublicKnowledgeSection } from "@shared/schema";

// Search results carry an extra `snippet` field with `<mark>...</mark>`
// markers around the matched terms, generated server-side.
type KnowledgeSearchResult = KnowledgeArticle & { snippet?: string };

// Search responses use a `{ items, suggestions }` envelope; suggestions
// power the "Did you mean…?" chips when items is empty (e.g. typo).
type KnowledgeSearchResponse = {
  items: KnowledgeSearchResult[];
  suggestions: KnowledgeSuggestion[];
};

interface PublicKnowledgePageProps {
  /** Which knowledge sections to surface on this public mirror. */
  sections: KnowledgeSection[];
  /** Page title and description. */
  title: string;
  description: string;
  /** Public-facing path prefix used to build deep links — e.g. "/help". */
  basePath: string;
  Icon: typeof BookOpen;
}

const SECTION_LABELS: Record<PublicKnowledgeSection, string> = {
  "getting-started": "Getting Started",
  "feature-guides": "Feature Guides",
  "security-training": "Security & Training",
  "compliance-policies": "Compliance & Policies",
};

function sortArticles(list: KnowledgeArticle[]): KnowledgeArticle[] {
  return [...list].sort((a, b) => {
    const oa = a.orderIndex ?? 0;
    const ob = b.orderIndex ?? 0;
    if (oa !== ob) return oa - ob;
    return a.title.localeCompare(b.title);
  });
}

function useDebouncedValue<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function PublicKnowledgePage({
  sections,
  title,
  description,
  basePath,
  Icon,
}: PublicKnowledgePageProps) {
  const [matchSlug, params] = useRoute<{ slug: string }>(`${basePath}/:slug`);
  const slug = matchSlug ? params?.slug : undefined;
  const [, navigate] = useLocation();

  // Read ?tag=… from the URL. We re-parse on every render so clicks on a tag
  // chip flip the page into "filtered" mode without a full reload.
  const searchString = useSearch();
  const activeTag = useMemo(() => {
    const sp = new URLSearchParams(searchString);
    const t = sp.get("tag")?.trim();
    return t ? t : null;
  }, [searchString]);

  const { data: articles = [], isLoading } = useQuery<KnowledgeArticle[]>({
    queryKey: ["/api/public/knowledge"],
  });

  const visibleArticles = useMemo(
    () => articles.filter((a) => sections.includes(a.section as KnowledgeSection)),
    [articles, sections],
  );

  const currentArticle = slug ? visibleArticles.find((a) => a.slug === slug) : undefined;

  const [manualSection, setManualSection] = useState<KnowledgeSection | null>(null);
  const activeSection: KnowledgeSection =
    (currentArticle?.section as KnowledgeSection | undefined) ??
    manualSection ??
    sections.find((s) => visibleArticles.some((a) => a.section === s)) ??
    sections[0];

  useEffect(() => {
    if (slug) setManualSection(null);
  }, [slug]);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250);
  const trimmedSearch = debouncedSearch.trim();

  // Server-side full-text search on the public corpus.
  const { data: serverResponse, isFetching: isSearching } = useQuery<KnowledgeSearchResponse>({
    queryKey: ["/api/public/knowledge", { q: trimmedSearch }],
    queryFn: async () => {
      const res = await fetch(`/api/public/knowledge?q=${encodeURIComponent(trimmedSearch)}`);
      if (!res.ok) throw new Error(`Search failed: ${res.status}`);
      return res.json();
    },
    enabled: trimmedSearch.length > 0,
    placeholderData: keepPreviousData,
  });
  const serverResults = serverResponse?.items;
  const serverSuggestions = serverResponse?.suggestions ?? [];

  // Server-side tag filter on the public corpus.
  const { data: tagResults, isFetching: isTagFetching } = useQuery<KnowledgeArticle[]>({
    queryKey: ["/api/public/knowledge", { tag: activeTag }],
    queryFn: async () => {
      const res = await fetch(`/api/public/knowledge?tag=${encodeURIComponent(activeTag!)}`);
      if (!res.ok) throw new Error(`Tag filter failed: ${res.status}`);
      return res.json();
    },
    enabled: Boolean(activeTag),
    placeholderData: keepPreviousData,
  });

  const sectionArticles = useMemo(
    () => sortArticles(visibleArticles.filter((a) => a.section === activeSection)),
    [visibleArticles, activeSection],
  );

  const searchResults = useMemo(() => {
    if (!trimmedSearch) return null;
    // Only include articles that belong to the sections this mirror surfaces.
    return (serverResults ?? []).filter((a) =>
      sections.includes(a.section as KnowledgeSection),
    );
  }, [trimmedSearch, serverResults, sections]);

  // Tag-filter results, scoped to sections this mirror surfaces. Null means
  // no tag filter is active and the page renders its normal section list.
  const taggedArticles = useMemo(() => {
    if (!activeTag) return null;
    return sortArticles(
      (tagResults ?? []).filter((a) => sections.includes(a.section as KnowledgeSection)),
    );
  }, [activeTag, tagResults, sections]);

  const clearTagFilter = () => {
    // Drop ?tag= but keep the path so the user stays on the article they were
    // reading (or on the section landing).
    navigate(slug ? `${basePath}/${slug}` : basePath, { replace: true });
  };

  // Apply a tag filter from the browse-by-tag cloud — pushes ?tag=X onto the
  // current path. Mirrors the link-renderer behavior used inside individual
  // articles' tag chips so clicks behave consistently across the page.
  const applyTagFilter = (tag: string) => {
    navigate(`${basePath}?tag=${encodeURIComponent(tag)}`);
  };

  // Build a distinct, frequency-ranked tag list from every visible article
  // so the public sidebar can offer a "browse by topic" affordance even
  // when the user hasn't started a search. Capped to keep the cloud
  // scan-able on smaller viewports.
  const tagCloud = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of visibleArticles) {
      for (const t of a.tags ?? []) {
        const key = t.trim();
        if (!key) continue;
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 24);
  }, [visibleArticles]);

  // Update the document title for client-side navigations so each article
  // gets a meaningful tab title (the server also injects this for crawlers).
  useEffect(() => {
    if (currentArticle) {
      document.title = `${currentArticle.title} — ${(SECTION_LABELS as Record<string, string>)[currentArticle.section] || "Knowledge"} — Clarik`;
    } else {
      document.title = `${title} — Clarik`;
    }
  }, [currentArticle, title]);

  return (
    <PublicShell>
      {/* Page header */}
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-4">
          <div className="rounded-lg border bg-card p-3">
            <Icon className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="heading-public">
              {title}
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9"
            data-testid="input-public-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover-elevate active-elevate-2"
              data-testid="button-public-clear-search"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Tag filter pill — shown above the layout when ?tag= is active. */}
      {activeTag && (
        <div
          className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm"
          data-testid="banner-public-tag-filter"
        >
          <span className="text-muted-foreground">Filtered by:</span>
          <Badge
            variant="secondary"
            className="gap-1.5 pl-2 pr-1 text-xs"
            data-testid="badge-public-active-tag"
          >
            <span className="font-medium">{activeTag}</span>
            <button
              type="button"
              onClick={clearTagFilter}
              className="ml-0.5 rounded-sm p-0.5 text-muted-foreground hover-elevate active-elevate-2"
              aria-label={`Remove tag filter ${activeTag}`}
              data-testid="button-public-clear-tag-filter"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
          {!isTagFetching && taggedArticles && (
            <span className="text-xs text-muted-foreground" data-testid="text-public-tag-count">
              {taggedArticles.length} article{taggedArticles.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
      )}

      {/* 3-pane layout (collapses to stacked on mobile) */}
      <div
        className="flex min-h-[60vh] flex-col overflow-hidden rounded-lg border bg-card md:flex-row"
        data-testid="layout-public-3pane"
      >
        {/* Pane 1: sections */}
        {sections.length > 1 && (
          <aside
            className="w-full shrink-0 border-b bg-muted/30 md:w-52 md:border-b-0 md:border-r"
            data-testid="pane-public-sections"
          >
            <ScrollArea className="h-full">
              <nav className="flex flex-row gap-1 p-2 md:flex-col">
                {sections.map((s) => {
                  const count = visibleArticles.filter((a) => a.section === s).length;
                  const isActive = activeSection === s && !searchResults && !taggedArticles;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setManualSection(s);
                        setSearch("");
                        if (activeTag) clearTagFilter();
                      }}
                      className={cn(
                        "flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover-elevate active-elevate-2",
                        isActive ? "bg-background font-medium text-foreground" : "text-muted-foreground",
                      )}
                      data-testid={`button-public-section-${s}`}
                    >
                      <span className="flex-1 truncate">{(SECTION_LABELS as Record<string, string>)[s]}</span>
                      <Badge variant="outline" className="text-xs">
                        {count}
                      </Badge>
                    </button>
                  );
                })}
              </nav>
              {tagCloud.length > 0 && (
                <div
                  className="border-t px-2 py-3 md:px-3"
                  data-testid="public-tag-cloud"
                >
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Browse by topic
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {tagCloud.map(([tag, count]) => {
                      const isActive = activeTag === tag;
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => applyTagFilter(tag)}
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors hover-elevate active-elevate-2",
                            isActive
                              ? "border-primary bg-primary/10 text-primary"
                              : "bg-background text-muted-foreground",
                          )}
                          aria-pressed={isActive}
                          data-testid={`button-public-tag-${tag}`}
                        >
                          <span className="truncate">{tag}</span>
                          <span className="text-[10px] opacity-70">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </ScrollArea>
          </aside>
        )}

        {/* Pane 2: article list */}
        <aside
          className="w-full shrink-0 border-b bg-background md:w-72 md:border-b-0 md:border-r"
          data-testid="pane-public-articles"
        >
          <ScrollArea className="h-full">
            <div className="p-3">
              {searchResults ? (
                <>
                  <div className="mb-2 px-1 text-xs uppercase tracking-wide text-muted-foreground">
                    <span data-testid="text-public-search-count">
                      Search · {isSearching ? "…" : `${searchResults.length} result${searchResults.length === 1 ? "" : "s"}`}
                    </span>
                  </div>
                  {!isSearching && searchResults.length === 0 && (
                    <div className="px-1">
                      <p className="text-sm text-muted-foreground" data-testid="text-no-public-search-results">
                        No matches.
                      </p>
                      <SuggestionChips
                        suggestions={serverSuggestions}
                        onSelect={setSearch}
                        containerTestId="suggestions-public-search"
                        buttonTestIdPrefix="button-public-suggestion"
                        originalQuery={search}
                      />
                    </div>
                  )}
                  <ul className="flex flex-col gap-1">
                    {searchResults.map((a) => (
                      <PublicArticleNavItem
                        key={a.id}
                        article={a}
                        basePath={basePath}
                        activeSlug={slug}
                        snippet={a.snippet}
                      />
                    ))}
                  </ul>
                </>
              ) : taggedArticles ? (
                <>
                  <div className="mb-2 px-1 text-xs uppercase tracking-wide text-muted-foreground">
                    Tag · {activeTag}
                  </div>
                  {!isTagFetching && taggedArticles.length === 0 && (
                    <p
                      className="px-1 text-sm text-muted-foreground"
                      data-testid="text-no-public-tag-results"
                    >
                      No public articles tagged "{activeTag}".
                    </p>
                  )}
                  <ul className="flex flex-col gap-1">
                    {taggedArticles.map((a) => (
                      <PublicArticleNavItem
                        key={a.id}
                        article={a}
                        basePath={basePath}
                        activeSlug={slug}
                      />
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <div className="mb-2 px-1 text-xs uppercase tracking-wide text-muted-foreground">
                    {(SECTION_LABELS as Record<string, string>)[activeSection]}
                  </div>
                  {isLoading ? (
                    <div className="space-y-1">
                      {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="h-7 animate-pulse rounded bg-muted" />
                      ))}
                    </div>
                  ) : sectionArticles.length === 0 ? (
                    <p className="px-1 text-sm text-muted-foreground">
                      Public content coming soon.
                    </p>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {sectionArticles.map((a) => (
                        <PublicArticleNavItem
                          key={a.id}
                          article={a}
                          basePath={basePath}
                          activeSlug={slug}
                        />
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </ScrollArea>
        </aside>

        {/* Pane 3: content */}
        <main className="min-w-0 flex-1 bg-background" data-testid="pane-public-content">
          <ScrollArea className="h-full">
            <div className="mx-auto max-w-3xl px-4 py-8 md:px-8">
              {slug ? (
                <PublicArticleView
                  slug={slug}
                  basePath={basePath}
                  backLabel={title}
                  sectionArticles={sectionArticles}
                />
              ) : (
                <PublicSectionLanding
                  section={activeSection}
                  basePath={basePath}
                  articles={sectionArticles}
                  isLoading={isLoading}
                />
              )}
            </div>
          </ScrollArea>
        </main>
      </div>
    </PublicShell>
  );
}

function PublicArticleNavItem({
  article,
  basePath,
  activeSlug,
  snippet,
}: {
  article: KnowledgeArticle;
  basePath: string;
  activeSlug: string | undefined;
  /** When provided, rendered below the title with matched terms highlighted. */
  snippet?: string;
}) {
  const isActive = article.slug === activeSlug;
  return (
    <li>
      <Link
        href={`${basePath}/${article.slug}`}
        className={cn(
          "flex flex-col gap-1 rounded-md px-2 py-1.5 text-sm transition-colors hover-elevate active-elevate-2",
          isActive ? "bg-accent font-medium text-accent-foreground" : "text-foreground/90",
        )}
        data-testid={`link-public-article-${article.slug}`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="truncate">{article.title}</span>
          {article.isDraft && (
            <Badge
              variant="outline"
              className="border-amber-400 text-[10px] text-amber-700 dark:text-amber-400"
            >
              DRAFT
            </Badge>
          )}
        </div>
        {snippet && (
          <HighlightedSnippet
            snippet={snippet}
            className="line-clamp-2"
            testId={`snippet-public-article-${article.slug}`}
          />
        )}
      </Link>
    </li>
  );
}

function PublicSectionLanding({
  section,
  basePath,
  articles,
  isLoading,
}: {
  section: KnowledgeSection;
  basePath: string;
  articles: KnowledgeArticle[];
  isLoading: boolean;
}) {
  return (
    <div data-testid={`section-public-${section}`}>
      <h2 className="text-2xl font-semibold">{(SECTION_LABELS as Record<string, string>)[section]}</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Choose an article from the left, or browse the cards below.
      </p>
      <div className="mt-6">
        {isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-5 w-2/3 rounded bg-muted" />
                  <div className="h-3 w-full rounded bg-muted" />
                </CardHeader>
              </Card>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              We are still publishing public content for this section. Check back soon.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {articles.map((a) => (
              <Link
                key={a.id}
                href={`${basePath}/${a.slug}`}
                className="block"
                data-testid={`card-public-article-${a.slug}`}
              >
                <Card className="h-full transition-colors hover-elevate active-elevate-2">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base leading-tight">{a.title}</CardTitle>
                      {a.isDraft && (
                        <Badge
                          variant="outline"
                          className="border-amber-400 text-amber-700 dark:text-amber-400"
                        >
                          DRAFT
                        </Badge>
                      )}
                    </div>
                    {a.summary && (
                      <CardDescription className="line-clamp-2">{a.summary}</CardDescription>
                    )}
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PublicShell({ children }: { children: React.ReactNode }) {
  // Task #558 + #560 — public knowledge surface honours both the
  // originating workspace's white-label brand (customEmailFromName ->
  // workspace.name -> Clarik) and the workspace's
  // "Powered by Clarik" attribution toggle. When the page is
  // accessed via a workspace's own custom domain, the
  // /api/branding/public endpoint resolves the workspace from the
  // request host and returns the workspace's brand fields + flag. On
  // the platform's own host the default workspace's values are
  // returned (which for the platform's own page keeps the credit on).
  const { data: branding } = useQuery<{
    workspaceName: string | null;
    customEmailFromName: string | null;
    showPoweredByAttribution?: boolean;
    poweredByText?: string | null;
  }>({
    queryKey: ["/api/branding/public"],
  });
  const year = new Date().getFullYear();
  const footerBrand =
    branding?.customEmailFromName?.trim() ||
    branding?.workspaceName?.trim() ||
    null;
  const footerCopyright = footerBrand
    ? `© ${year} ${footerBrand}`
    : `© ${year} Clarik`;
  const showAttribution = branding?.showPoweredByAttribution ?? true;
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
      <header className="border-b bg-background/80 backdrop-blur">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <Link href="/welcome" className="flex items-center gap-2" data-testid="link-public-home">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
              <Star className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold">Clarik</span>
          </Link>
          <nav className="hidden gap-4 text-sm text-muted-foreground md:flex">
            <Link href="/help" className="hover:text-foreground" data-testid="link-help">Help</Link>
            <Link href="/security" className="hover:text-foreground" data-testid="link-security">Security</Link>
            <Link href="/trust" className="hover:text-foreground" data-testid="link-trust">Trust</Link>
            <Link href="/policies" className="hover:text-foreground" data-testid="link-policies">Policies</Link>
          </nav>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => (window.location.href = "/login")} data-testid="button-public-signin">
              Sign in
            </Button>
          </div>
        </div>
      </header>
      <main className="container mx-auto max-w-7xl px-4 py-10">{children}</main>
      <footer className="border-t bg-muted/30 py-6">
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-3 px-4 text-sm text-muted-foreground">
          <div className="flex flex-col">
            <p data-testid="text-public-knowledge-copyright">{footerCopyright}</p>
            <p data-testid="text-public-footer-brand" className="sr-only">
              {footerCopyright}
            </p>
            {showAttribution ? (
              <p
                className="text-xs text-muted-foreground/70 tracking-wide"
                data-testid="text-powered-by-attribution"
              >
                {branding?.poweredByText?.trim() || "Powered by Clarik.io"}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-4">
            <Link href="/help" className="hover:underline" data-testid="footer-link-help">Help</Link>
            <Link href="/security" className="hover:underline" data-testid="footer-link-security">Security</Link>
            <Link href="/trust" className="hover:underline" data-testid="footer-link-trust">Trust</Link>
            <Link href="/policies" className="hover:underline" data-testid="footer-link-policies">Policies</Link>
            <Link href="/terms" className="hover:underline" data-testid="footer-link-terms">Terms</Link>
            <Link href="/privacy" className="hover:underline" data-testid="footer-link-privacy">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PublicArticleView({
  slug,
  basePath,
  backLabel,
  sectionArticles,
}: {
  slug: string;
  basePath: string;
  backLabel: string;
  sectionArticles: KnowledgeArticle[];
}) {
  const [, navigate] = useLocation();
  const { data: article, isLoading, error } = useQuery<KnowledgeArticle>({
    queryKey: ["/api/public/knowledge", slug],
  });

  // Server-computed related articles (same section + shared tags).
  const { data: related = [] } = useQuery<KnowledgeArticle[]>({
    queryKey: ["/api/public/knowledge", slug, "related"],
    enabled: Boolean(article),
  });

  const { prev, next } = useMemo(() => {
    if (!article) return { prev: undefined, next: undefined };
    const idx = sectionArticles.findIndex((a) => a.id === article.id);
    return {
      prev: idx > 0 ? sectionArticles[idx - 1] : undefined,
      next: idx >= 0 && idx < sectionArticles.length - 1 ? sectionArticles[idx + 1] : undefined,
    };
  }, [article, sectionArticles]);

  if (isLoading) {
    return (
      <div>
        <div className="h-8 w-1/2 animate-pulse rounded bg-muted" />
        <div className="mt-3 h-4 w-3/4 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-center">
        <p className="text-muted-foreground">This article is not currently public.</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate(basePath)}
          data-testid="button-back-public"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to {backLabel}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight" data-testid="text-public-article-title">
        {article.title}
      </h1>
      {article.summary && (
        <p className="mt-2 text-lg text-muted-foreground">{article.summary}</p>
      )}

      {article.tags && article.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="public-article-tags">
          {article.tags.map((t) => (
            <Link
              key={t}
              href={`${basePath}?tag=${encodeURIComponent(t)}`}
              data-testid={`link-public-article-tag-${t}`}
            >
              <Badge
                variant="outline"
                className="cursor-pointer text-xs transition-colors hover-elevate active-elevate-2"
                title={`Browse public articles tagged "${t}"`}
              >
                {t}
              </Badge>
            </Link>
          ))}
        </div>
      )}

      {article.isDraft && (
        <div
          className="my-6 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/30"
          data-testid="banner-public-draft"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-400" />
          <div className="text-sm">
            <div className="font-semibold text-amber-900 dark:text-amber-200">
              DRAFT — pending legal & compliance review
            </div>
            <div className="text-amber-900/90 dark:text-amber-200/90">
              We publish working drafts in the open while they are under review by our
              counsel. Final language may change before this becomes binding.
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        <ArticleRenderer markdown={article.bodyMarkdown} videoUrl={article.videoUrl} />
      </div>

      {(prev || next) && (
        <div
          className="mt-10 grid gap-3 border-t pt-6 sm:grid-cols-2"
          data-testid="nav-public-article-pagination"
        >
          {prev ? (
            <Link href={`${basePath}/${prev.slug}`} data-testid={`link-public-prev-article-${prev.slug}`}>
              <Card className="h-full transition-colors hover-elevate active-elevate-2">
                <CardHeader>
                  <CardDescription className="flex items-center gap-1 text-xs">
                    <ArrowLeft className="h-3 w-3" /> Previous
                  </CardDescription>
                  <CardTitle className="text-sm leading-tight">{prev.title}</CardTitle>
                </CardHeader>
              </Card>
            </Link>
          ) : (
            <div />
          )}
          {next ? (
            <Link href={`${basePath}/${next.slug}`} data-testid={`link-public-next-article-${next.slug}`}>
              <Card className="h-full transition-colors hover-elevate active-elevate-2 sm:text-right">
                <CardHeader>
                  <CardDescription className="flex items-center gap-1 text-xs sm:justify-end">
                    Next <ArrowRight className="h-3 w-3" />
                  </CardDescription>
                  <CardTitle className="text-sm leading-tight">{next.title}</CardTitle>
                </CardHeader>
              </Card>
            </Link>
          ) : (
            <div />
          )}
        </div>
      )}

      {related.length > 0 && (
        <div className="mt-10 border-t pt-6" data-testid="related-public-articles">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Related articles
          </h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {related.map((r) => {
              const baseForRelated = inferMirrorForSection(
                r.section as KnowledgeSection,
                basePath,
              );
              return (
                <Link
                  key={r.id}
                  href={`${baseForRelated}/${r.slug}`}
                  className="block"
                  data-testid={`link-public-related-${r.slug}`}
                >
                  <Card className="h-full transition-colors hover-elevate active-elevate-2">
                    <CardHeader>
                      <CardTitle className="text-sm leading-tight">{r.title}</CardTitle>
                      {r.summary && (
                        <CardDescription className="line-clamp-2 text-xs">
                          {r.summary}
                        </CardDescription>
                      )}
                    </CardHeader>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Pick a sensible public mirror path for a related article whose section may
 * not be the same as the current page. Falls back to the current `basePath`
 * if no mapping is appropriate (the article will still load there).
 */
function inferMirrorForSection(section: KnowledgeSection, fallback: string): string {
  switch (section) {
    case "getting-started":
    case "feature-guides":
      return "/help";
    case "security-training":
      return fallback === "/trust" ? "/trust" : "/security";
    case "compliance-policies":
      return fallback === "/trust" ? "/trust" : "/policies";
    default:
      return fallback;
  }
}

// Pre-bound exports for the four public mirrors
export function HelpPage() {
  return (
    <PublicKnowledgePage
      sections={["getting-started", "feature-guides"]}
      title="Help & Getting Started"
      description="Walkthroughs and guides for using Clarik — for booking agents, artists, venue staff, and admins."
      basePath="/help"
      Icon={BookOpen}
    />
  );
}

export function SecurityPage() {
  return (
    <PublicKnowledgePage
      sections={["security-training"]}
      title="Security at Clarik"
      description="How we protect customer and audience data — encryption, access controls, monitoring, and the security training every employee completes."
      basePath="/security"
      Icon={ShieldCheck}
    />
  );
}

export function TrustPage() {
  return (
    <PublicKnowledgePage
      sections={["security-training", "compliance-policies"]}
      title="Trust Center"
      description="Our public commitments: security posture, compliance roadmap, and the policies that govern how we operate."
      basePath="/trust"
      Icon={Award}
    />
  );
}

export function PoliciesPage() {
  return (
    <PublicKnowledgePage
      sections={["compliance-policies"]}
      title="Policies & Compliance"
      description="Policies aligned to SOC2 / SEC-relevant controls. Counsel-reviewed and published; the full sign-off log is tracked in docs/legal-review-notes.md §6."
      basePath="/policies"
      Icon={Scale}
    />
  );
}
